import { formatDistanceToNow } from 'date-fns'
import { ChevronRight, ExternalLink } from 'lucide-react'
import pluralize from 'pluralize'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { DotSpinner } from '@/components/ui/DotSpinner'
import { Separator } from '@/components/ui/separator'
import { formatCurrencyRange, formatDate } from '../../lib/formatters'
import { cn, formatCurrency } from '../../lib/utils'
import { useCarrierAttestations } from '../../models/attestation'
import { CARRIER_NIRVANA_ENABLED, useFeatureFlag } from '../../models/flag'
import { COVERAGE_TYPE_LABELS, CoverageType } from '../../models/insuranceCoverage'
import { type DeclineDetail, getActiveQuote, InsuranceIndication } from '../../models/insuranceIndication'
import { InsuranceQuote } from '../../models/insuranceQuote'
import { InsuranceIndicationStatus, InsuranceQuoteStatus, Vendor } from '../../models/insuranceTypes'
import { CarrierAttestationDialog } from '../quotes/CarrierAttestationDialog'
import { isQuoteExpired } from '../quotes/quoteCardTypes'
import { CORGI_EDITED_MESSAGE, corgiResubmitState, corgiSyncNeed, isCorgiSyncable } from './corgiResubmit'
import { DECLINE_BADGE_CLASSES, DECLINE_CATEGORY_LABELS, DeclineBadge, declineBadge } from './declineDisplay'
import { IndicationScreenshotsDialog } from './IndicationScreenshotsDialog'
import { IndicationTimestamps } from './OfferTimestamps'
import { QuoteDocumentsDialog } from './QuoteDocumentsDialog'
import { RawCarrierError } from './RawCarrierError'
import { ResubmitIndicationButton } from './ResubmitIndicationButton'
import { SendCorgiDocumentsDialog } from './SendCorgiDocumentsDialog'
import { SyncWithCorgiButton } from './SyncWithCorgiButton'

const CW_COVERAGE_LABELS: Record<string, string> = {
  al: 'Auto Liability',
  apd: 'Physical Damage',
  mtc: 'Cargo',
  tgl: 'General Liability',
  ntl: 'Non-Trucking Liability',
}

// Keyed camelCase because these are object *keys* inside manualReviewReasons /
// declinedCoverages, and humps camelizes keys. The maps below stay snake_case:
// they're keyed by values (a document_type, a status, a reason), which humps
// leaves untouched.
const CORGI_COVERAGE_LABELS: Record<string, string> = {
  autoLiabilityPowerUnit: 'Commercial Auto Liability',
  cargoContractualLiability: 'Cargo Contractual Liability',
  generalLiability: 'General Liability',
  physicalDamage: 'Physical Damage',
}

const CORGI_MANUAL_REVIEW_LABELS: Record<string, string> = {
  underwriting_review: 'Held for underwriter review',
  submission_incomplete: 'Submission incomplete — fix and resubmit',
  vehicle_data_unverified: 'Vehicle data could not be verified',
  declared_operations_review: 'Declared operations require review',
  data_verification_review: 'Data disagrees with FMCSA records',
}

const CORGI_DECLINED_LABELS: Record<string, string> = {
  underwriting_declined: 'Declined by underwriting',
}

const CORGI_REQUIRED_DOC_LABELS: Record<string, string> = {
  loss_run: 'Loss Run',
  ifta: 'IFTA Report',
  vehicle_registration: 'Vehicle Registration',
  driver_license: "Driver's License",
  mvr: 'Motor Vehicle Record',
  acord_application: 'ACORD Application',
  prior_policy: 'Prior Policy',
}

interface DeclineVerdict {
  badge: DeclineBadge
  badgeClassName: string
  nextStep: string
}

/**
 * What staff can do about a decline. A fixable Corgi decline on a quote Corgi accepted
 * can't be resubmitted, so it is fixed in the portal; an expired one starts a new quote.
 */
function declineVerdict(
  detail: DeclineDetail,
  vendorName: string,
  { isCorgi, corgiHoldsQuote, expired }: { isCorgi: boolean; corgiHoldsQuote: boolean; expired: boolean }
): DeclineVerdict {
  const badge = declineBadge(detail)
  if (badge === DeclineBadge.Unknown) {
    return {
      badge,
      badgeClassName: DECLINE_BADGE_CLASSES[badge],
      nextStep: isCorgi
        ? 'Open the quote in the Corgi portal to see why.'
        : `Check with ${vendorName} to see why.`,
    }
  }
  if (badge === DeclineBadge.Final) {
    return {
      badge,
      badgeClassName: DECLINE_BADGE_CLASSES[badge],
      nextStep: "Resubmitting won't help.",
    }
  }
  if (corgiHoldsQuote) {
    return {
      badge: DeclineBadge.FixInPortal,
      badgeClassName: DECLINE_BADGE_CLASSES[DeclineBadge.FixInPortal],
      nextStep: 'Fix it in the Corgi portal, then press Sync.',
    }
  }
  return {
    badge,
    badgeClassName: DECLINE_BADGE_CLASSES[badge],
    nextStep: expired
      ? 'Fix it in Cai, then press Resubmit to start a new quote.'
      : 'Fix it in Cai, then press Resubmit.',
  }
}

const CORGI_REQUIRED_DOC_STATUS_LABELS: Record<string, string> = {
  required: 'Required',
  waived: 'Waived',
  provided: 'Provided',
  pending: 'Pending',
}

/**
 * The carrier name beside whatever status and actions the card's state carries.
 * A flex row, not a corner pinned with `absolute`: a long carrier name used to run
 * straight underneath the status and View details.
 */
function IndicationHeader({ actions, children }: { actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      {/* min-w-40 is what wraps the row: below it the actions drop to their own line. */}
      <div className="min-w-40 flex-1">{children}</div>
      {actions ? (
        <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-x-4 gap-y-1">
          {actions}
        </div>
      ) : null}
    </div>
  )
}

export interface ChatIndicationCardProps {
  indication: InsuranceIndication
  isSelected: boolean
  hideCheckbox: boolean
  onToggleSelect: (indication: InsuranceIndication) => void
  /** The detail to show, plus the indication behind it — the sidebar renders
   * that indication's actions, and the first argument may be its quote. */
  onViewDetails: (detail: InsuranceIndication | InsuranceQuote, indication: InsuranceIndication) => void
  children?: React.ReactNode
  showQuotes?: boolean
  hideLogo?: boolean
  hideViewDetails?: boolean
  isStaff?: boolean
  isCaiAdmin?: boolean
  applicationId?: string
  /** Needed for the card's "Send to Corgi" action — omitted, it doesn't render. */
  orgId?: string
  readonly?: boolean
  /** Staff only: when the application last changed, to flag a Corgi draft with unsent edits. */
  applicationEditedAt?: string | null
}

export function ChatIndicationCard({
  indication,
  isSelected,
  hideCheckbox,
  onToggleSelect,
  onViewDetails,
  children,
  showQuotes,
  hideLogo,
  hideViewDetails,
  isStaff,
  isCaiAdmin,
  applicationId,
  orgId,
  readonly,
  applicationEditedAt,
}: ChatIndicationCardProps) {
  const activeQuote = getActiveQuote(indication)
  // Metadata arrives camelized: every fetch runs the payload through
  // toCamelCase, and humps rewrites nested keys, so the backend's
  // is_manual_review / required_documents land here as isManualReview /
  // requiredDocuments. Reading the snake_case names left these Corgi states
  // permanently invisible.
  const manualReviewReasons = (activeQuote?.metadata?.manualReviewReasons ?? {}) as Record<string, string[]>
  const declinedCoverages = (activeQuote?.metadata?.declinedCoverages ?? {}) as Record<string, string>
  const isCorgiManualReview = indication.vendor === Vendor.CORGI && !!indication.metadata?.isManualReview
  const isCorgiNeedsInfo = indication.vendor === Vendor.CORGI && !!indication.metadata?.isNeedsInfo
  // Our prequote, not the carrier's price: every carrier result lands as GENERATED,
  // so premiums on anything else were put there by us.
  const estimatedCoverages =
    indication.status === InsuranceIndicationStatus.GENERATED
      ? []
      : (indication.coverages ?? []).filter((coverage) => coverage.premium)
  const estimate = estimatedCoverages.length > 0
  // It used to say "documents" whatever was outstanding, including when the answer
  // was the carrier's terms, or when nothing was outstanding at all.
  const needsInfoReason = indication.needsAttestation
    ? 'Corgi needs the customer to accept its terms before it can price this.'
    : (indication.outstandingDocuments ?? []).length > 0
      ? 'Corgi requires additional documents before this quote can be processed.'
      : 'Corgi is still working on this quote.'
  // Rocklake email-driven status is staff-only for now (per the ingestion
  // plan's Phase 2) — customers still see the plain PENDING spinner state.
  const isRocklakeSuspended =
    isStaff && indication.vendor === Vendor.ROCKLAKE && !!indication.metadata?.suspended
  const isRocklakeReleased =
    isStaff && indication.vendor === Vendor.ROCKLAKE && !!indication.metadata?.released
  const requiredDocuments = (indication.metadata?.requiredDocuments ?? []) as Array<{
    documentType: string
    status: string
  }>
  // Corgi's own words for each thing it asked for, such as a loss run that is too old.
  const corgiNeedsInfoMessages = (
    (indication.metadata.needsInfoItems ?? []) as Array<string | { message?: string }>
  )
    .map((item) => (typeof item === 'string' ? item : item.message))
    .filter((message): message is string => !!message)
  // An estimate on a failed card makes "Estimation unavailable" a lie — what is
  // missing is the quote behind it, not an estimate sitting right below the words.
  const unavailableSubtitle = showQuotes || estimate ? 'Quote unavailable' : 'Estimation unavailable'
  const checkboxVisible =
    !hideCheckbox &&
    indication.status === InsuranceIndicationStatus.GENERATED &&
    (showQuotes ? activeQuote?.status === InsuranceQuoteStatus.QUOTED : true)
  const indicationOrQuote = showQuotes ? activeQuote || indication : indication
  const truckCount = indication.metadata.truckCount ?? 0
  const showPerTruck = truckCount > 1
  const gridCols = showPerTruck ? 'grid-cols-[150px_1fr_1fr]' : 'grid-cols-[150px_1fr]'

  // Gated on isCaiAdmin, not isStaff: the resubmit-indications endpoint
  // itself requires is_cai_admin() and 403s otherwise — showing this to any
  // staff user would render a dead action for non-admin staff. Also excluded
  // once bound — resubmitting a live bound policy is a different, riskier
  // operation than resubmitting an indication.
  // While Nirvana is turned off, the backend refuses a Nirvana resubmit, so the button would only fail.
  const nirvanaEnabled = useFeatureFlag(CARRIER_NIRVANA_ENABLED)
  const canResubmit =
    !!(isCaiAdmin && applicationId) &&
    activeQuote?.status !== InsuranceQuoteStatus.BOUND &&
    (indication.vendor !== Vendor.NIRVANA || nirvanaEnabled)
  const declineOrErrorReason: string | null = indication.metadata.error ?? indication.declineReasonRaw ?? null
  const corgiResubmit = isStaff ? corgiResubmitState(indication, applicationEditedAt) : null
  // Same Corgi dates the quote card reads.
  const corgiDates = {
    carrierExpired: !!indication.metadata.corgiExpired,
    expiresAt: indication.metadata.corgiQuoteDates?.expiresAt ?? null,
  }
  const corgiLastSyncedAt: string | undefined = indication.metadata.corgiLastSyncedAt

  // Same admin gate as Resubmit — sending a document to a live Corgi quote is
  // an equally consequential write, and this button is currently Corgi-only.
  const canSendCorgiDocuments = !!(isCaiAdmin && orgId) && indication.vendor === Vendor.CORGI
  // Same admin gate: a Sync can re-link the card to another Corgi quote.
  const canSyncCorgi = !!(isCaiAdmin && orgId) && isCorgiSyncable(indication)

  const [attestationOpen, setAttestationOpen] = useState(false)
  // Same rule as the new quote card: a quote is the carrier's firm answer, so terms
  // outstanding on the indication no longer block anything once one exists.
  const mayNeedAttestation = !!indication.needsAttestation && !!orgId && !readonly && !activeQuote
  const { templates: attestationTemplates } = useCarrierAttestations(mayNeedAttestation ? orgId : undefined)
  // Only offer terms we can actually render — the dialog's templates come from the
  // carrier, so without them Review opens on nothing.
  const needsAttestation = mayNeedAttestation && attestationTemplates.length > 0
  // Staff only, on every state: the customer's card asks for the carrier's terms whenever the
  // carrier has not answered, so staff can see what the customer is waiting on.
  const customerOwesTerms =
    !!isStaff &&
    !!indication.needsAttestation &&
    !activeQuote &&
    indication.status !== InsuranceIndicationStatus.DECLINED &&
    indication.status !== InsuranceIndicationStatus.ERROR &&
    indication.status !== InsuranceIndicationStatus.UNAVAILABLE
  const pendingTermTypes = (indication.metadata.pendingAttestationTypes ?? []) as string[]

  function renderStaffTermsNote() {
    if (!customerOwesTerms) return null
    const terms = pendingTermTypes.map((termType) => termType.replaceAll('_', ' ')).join(', ')
    return (
      <Banner variant="warning" small>
        Waiting on the customer to accept {indication.vendorName}&apos;s terms{terms ? `: ${terms}` : ''}.
      </Banner>
    )
  }

  // Staff only: the one thing that tells a Rocklake-placed Progressive from a direct one.
  function renderBrokerBadge() {
    // A Rocklake row with no carrier yet is titled "Rocklake" already.
    if (!isStaff || !indication.broker || indication.broker === indication.vendorName) return null
    return (
      <Badge variant="outline" className="ml-2 text-xs">
        via {indication.broker}
      </Badge>
    )
  }

  // Staff only: the backend sends isOutOfDate to Cai admins alone.
  function renderOutOfDateBadge() {
    if (!indication.isOutOfDate) return null
    return (
      <Badge variant="warning" className="ml-2 text-xs">
        Older version
      </Badge>
    )
  }

  // Expired is the carrier's word, so customers see it too. Prequote is for staff: the
  // estimate came from Corgi's prequote, and Cai has not submitted the quote yet.
  function renderCorgiDateBadges() {
    const acceptedQuoteId = indication.metadata.corgiAcceptedQuoteId as string | undefined
    const isSubmitted = !!acceptedQuoteId && acceptedQuoteId === indication.vendorId
    const isPrequote =
      !!isStaff &&
      indication.vendor === Vendor.CORGI &&
      estimate &&
      !!indication.metadata?.corgiPrequote &&
      !isSubmitted
    return (
      <>
        {isPrequote && (
          <Badge variant="outline" className="ml-2 text-xs">
            Prequote
          </Badge>
        )}
        {isQuoteExpired(corgiDates) && (
          <Badge variant="secondary" className="ml-2 text-xs">
            Expired
          </Badge>
        )}
      </>
    )
  }

  function renderResubmitButton() {
    if (!canResubmit) return null
    const button = (
      <ResubmitIndicationButton
        vendor={indication.vendor as Vendor}
        vendorName={indication.vendorName}
        applicationId={applicationId!}
        orgId={orgId}
        // A Rocklake card is one line of one send, so Resubmit reruns that line alone.
        coverageLine={
          indication.vendor === Vendor.ROCKLAKE ? (indication.coverageLine ?? undefined) : undefined
        }
        disabledReason={corgiResubmit?.blockedReason}
        emphasized={corgiResubmit?.editedSinceSubmit}
      />
    )
    if (!corgiResubmit?.editedSinceSubmit) return button
    return (
      <span className="flex flex-wrap items-center gap-2">
        {button}
        <span className="text-warning-d1 text-xs">{CORGI_EDITED_MESSAGE}</span>
      </span>
    )
  }

  function renderSyncCorgiButton() {
    if (!canSyncCorgi) return null
    return (
      <SyncWithCorgiButton
        orgId={orgId!}
        applicationId={applicationId}
        indication={indication}
        needReason={corgiSyncNeed(indication)}
      />
    )
  }

  // Resubmit and Sync with Corgi sit together wherever a card offers Resubmit.
  function renderResubmitActions() {
    const resubmit = renderResubmitButton()
    const sync = renderSyncCorgiButton()
    if (!resubmit || !sync) return resubmit ?? sync
    return (
      <span className="flex items-center gap-2">
        {resubmit}
        {sync}
      </span>
    )
  }

  function renderSendCorgiDocumentsButton() {
    if (!canSendCorgiDocuments) return null
    return (
      <SendCorgiDocumentsDialog orgId={orgId!} indicationId={indication.id} applicationId={applicationId}>
        <Button size="sm" variant="outline">
          Send documents to Corgi
        </Button>
      </SendCorgiDocumentsDialog>
    )
  }

  // Prefer the deep link that reopens this specific quote — the carrier's
  // resume URL, resolved from its retention id when the run finished. The
  // provider portal link only reaches the dashboard root, leaving you to search
  // for the quote by hand.
  function renderPortalLink() {
    // Responses run through toCamelCase, which camelizes nested metadata keys
    // too — the backend writes geico_dashboard_url.
    const quoteUrl = indication.metadata.geicoDashboardUrl as string | undefined
    const href = quoteUrl ?? indication.portalLink?.url
    if (!isStaff || !href) return null
    const provider = indication.portalLink?.providerLabel ?? indication.vendorName
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-ink hover:text-ink/80 flex items-center gap-1 text-sm whitespace-nowrap"
      >
        View {provider} {quoteUrl ? 'Quote' : 'Portal'}
        <ExternalLink className="h-3 w-3" />
      </a>
    )
  }

  function renderScreenshotsDialog() {
    if (!isStaff) return null
    return <IndicationScreenshotsDialog indication={indication} />
  }

  function renderQuoteDocumentsDialog() {
    if (!isStaff) return null
    return <QuoteDocumentsDialog indication={indication} />
  }

  // Manual stopgap until the portal-notes reply flow ships: staff open the
  // Rocklake portal directly to post a reply to an underwriter note.
  function renderPortalNoteLink() {
    if (!isStaff || !indication.portalNoteUrl) return null
    return (
      <a
        href={indication.portalNoteUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-ink hover:text-ink/80 flex items-center gap-1 text-sm whitespace-nowrap"
      >
        View portal note
        <ExternalLink className="h-3 w-3" />
      </a>
    )
  }

  // Resubmit and Send documents to Corgi stay on the card for a failed indication (Cai-admin only); unavailable gets nothing since both would fail silently.
  function renderPortalActions(includeResubmit: boolean) {
    const sendDocuments = includeResubmit ? renderSendCorgiDocumentsButton() : null
    const resubmit = includeResubmit ? renderResubmitActions() : null
    if (!sendDocuments && !resubmit) return null
    return (
      <div className="flex items-center gap-4">
        {sendDocuments}
        {resubmit}
      </div>
    )
  }

  // Declined / unavailable / errored all render the same box — same type sizes,
  // same spacing — differing only in the reason line and whether a resubmit is
  // offered. Keeping one shape matters beyond tidiness: the three statuses are
  // interchangeable run-to-run for a given vendor (a sandbox API that declines
  // on one call times out on the next), so per-branch spacing/weight drift
  // showed up as a layout shift in the visual-regression captures.
  /** The carrier's own numbers, whatever the submission went on to do with them.
   *
   * Shown on every state that isn't priced, including a failure: the estimate is a
   * real price the carrier put on this DOT, and the two pages disagreeing about
   * whether a carrier had a number at all is worse than either answer.
   */
  function renderEstimate() {
    if (!estimate) return null
    const total = estimatedCoverages.reduce((sum, coverage) => sum + Number(coverage.premium ?? 0), 0)
    return (
      <div className="flex flex-col gap-2 text-sm">
        <span className="text-accent-cyan font-semibold">Carrier estimate</span>
        {estimatedCoverages.map((coverage) => (
          <div key={coverage.id} className="grid grid-cols-2 gap-2">
            <span className="text-muted-foreground">
              {COVERAGE_TYPE_LABELS[coverage.coverageType as CoverageType] ?? coverage.label}
            </span>
            <span className="text-general-foreground">
              {coverage.premiumRange
                ? formatCurrencyRange(coverage.premiumRange)
                : formatCurrency(Number(coverage.premium))}
            </span>
          </div>
        ))}
        <Separator className="my-1" />
        <div className="grid grid-cols-2 gap-2 font-semibold">
          <span className="text-muted-foreground">Estimated total</span>
          <span className="text-general-foreground">
            {indication.premiumRange ? formatCurrencyRange(indication.premiumRange) : formatCurrency(total)}
          </span>
        </div>
        <span className="text-muted-foreground text-xs">
          An estimate from the carrier's own pricing, not a quote you can bind.
        </span>
      </div>
    )
  }

  function renderUnavailableState() {
    const { status } = indication
    const isDeclined = status === InsuranceIndicationStatus.DECLINED
    const isUnavailable = status === InsuranceIndicationStatus.UNAVAILABLE
    const isError = status === InsuranceIndicationStatus.ERROR
    if (!isDeclined && !isUnavailable && !isError) return null

    let reason: string
    if (isDeclined) {
      // The fallback covers indications declined before categorisation shipped.
      reason =
        indication.declineReason ??
        'This carrier does not meet the eligibility requirements for this provider.'
    } else if (isUnavailable) {
      reason = "This provider doesn't currently offer commercial auto policies in your state."
    } else if (estimate) {
      reason = `We couldn't get a full quote from ${indication.vendorName}. Cai is looking into it.`
    } else {
      reason = `We couldn't reach ${indication.vendorName} to retrieve an estimate.`
    }

    // Corgi never accepted this draft, so staff fix the reason and Resubmit the same draft.
    const draftError = isError ? corgiResubmit?.draftError : null
    const declineDetail = indication.declineDetail
    // The carrier's reason in plain words; the raw text it came from stays one click away.
    const carrierReason = declineDetail?.carrierReason ?? null
    const shownReason = carrierReason ?? declineOrErrorReason ?? ''
    // A note that only repeats the reason adds nothing.
    const staffNote =
      declineDetail?.staffNote && !shownReason.includes(declineDetail.staffNote)
        ? declineDetail.staffNote
        : null
    const vendorMessage = isStaff && (declineOrErrorReason || declineDetail) && !isUnavailable
    const failureReason = indication.failureReason
    // Verbatim, so session ids and JSON wrap where they can and scroll sideways where they can't.
    const verdict =
      declineDetail &&
      declineVerdict(declineDetail, indication.vendorName, {
        isCorgi: indication.vendor === Vendor.CORGI,
        corgiHoldsQuote: !!corgiResubmit?.blockedReason,
        expired: isQuoteExpired(corgiDates),
      })
    const carrierText = (
      <Banner variant={isError ? 'error' : 'warning'} small>
        {declineDetail && verdict && (
          <div className="mb-1 flex items-center justify-between gap-2 border-b border-dashed border-current/30 pb-1">
            <span className="text-[11px] font-bold tracking-wide uppercase">
              {DECLINE_CATEGORY_LABELS[declineDetail.category]}
            </span>
            <Badge variant="outline" className={verdict.badgeClassName}>
              {verdict.badge}
            </Badge>
          </div>
        )}
        {carrierReason ? (
          <div>{carrierReason}</div>
        ) : (
          declineOrErrorReason && (
            <div className="overflow-x-auto break-words whitespace-pre-wrap">{declineOrErrorReason}</div>
          )
        )}
        {staffNote && <div className="text-foreground/80 mt-1">{staffNote}</div>}
        {verdict && <div className="text-foreground/80 mt-1">{verdict.nextStep}</div>}
        {carrierReason && declineOrErrorReason && declineOrErrorReason !== carrierReason && (
          <Collapsible className="group/full-row mt-1 flex min-w-0 flex-col gap-1">
            <CollapsibleTrigger className="focus-visible:ring-ring/50 flex cursor-pointer items-center gap-1 self-start rounded-sm text-xs font-medium outline-none focus-visible:ring-[3px]">
              <ChevronRight className="size-3.5 transition-transform duration-200 group-data-[state=open]/full-row:rotate-90" />
              Show full row
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-x-auto break-words whitespace-pre-wrap">
              {declineOrErrorReason}
            </CollapsibleContent>
          </Collapsible>
        )}
      </Banner>
    )

    return (
      // min-w-0: this is the card's grid column, and a grid item defaults to
      // min-width:auto — so an unbreakable run of JSON widens the column itself
      // and no amount of wrapping inside it helps.
      <div className="flex min-w-0 flex-col gap-4">
        {/* Top right, where every other card carries it — a failed indication
            has no premium worth a sidebar, but it does have the carrier link,
            the screenshots, the vendor id and the CTAs, which is exactly what
            you want when one fails. */}
        <IndicationHeader
          actions={
            isCaiAdmin &&
            !hideViewDetails && (
              <Button size="sm" variant="link" onClick={() => onViewDetails(indication, indication)}>
                View details
              </Button>
            )
          }
        >
          <div className="text-general-foreground space-y-1">
            <div className="text-md font-semibold">
              {indication.vendorName}
              {renderBrokerBadge()}
              {renderOutOfDateBadge()}
              {renderCorgiDateBadges()}
            </div>
            {draftError ? (
              <div className="text-warning-d1 text-md font-semibold">Draft: needs a fix</div>
            ) : (
              <div className="text-muted-foreground text-md font-medium">{unavailableSubtitle}</div>
            )}
          </div>
        </IndicationHeader>
        {renderEstimate()}
        {/* A draft's reason is what staff must fix, so it stays in view rather than folded away. */}
        {vendorMessage && (!failureReason || draftError) ? (
          carrierText
        ) : (
          <div className="text-muted-foreground space-y-1 text-sm font-medium">{failureReason ?? reason}</div>
        )}
        {vendorMessage && failureReason && !draftError && <RawCarrierError>{carrierText}</RawCarrierError>}
        {renderPortalActions(!isUnavailable)}
      </div>
    )
  }

  function renderContent() {
    // Declined / unavailable / errored indications still render their action row
    // (children) so staff can edit or delete an auto-generated indication.
    if (
      indication.status === InsuranceIndicationStatus.DECLINED ||
      indication.status === InsuranceIndicationStatus.UNAVAILABLE ||
      indication.status === InsuranceIndicationStatus.ERROR
    ) {
      return (
        <>
          {renderUnavailableState()}
          {children && <div className="mt-4 w-full">{children}</div>}
        </>
      )
    }

    if (indication.status === InsuranceIndicationStatus.PENDING) {
      if (isCorgiNeedsInfo) {
        return (
          <div className="flex flex-col gap-4">
            <div className="text-general-foreground space-y-1">
              <div className="text-md font-semibold">
                {indication.vendorName}
                {renderBrokerBadge()}
                {renderOutOfDateBadge()}
                {renderCorgiDateBadges()}
              </div>
              <div className="text-warning-d1 text-md font-semibold">Needs Information</div>
              <div className="text-muted-foreground text-sm">{needsInfoReason}</div>
            </div>
            {renderEstimate()}
            {renderStaffTermsNote()}
            {isStaff && corgiNeedsInfoMessages.length > 0 && (
              <div className="space-y-1">
                <span className="text-accent-cyan text-sm font-semibold">Corgi says</span>
                {corgiNeedsInfoMessages.map((message) => (
                  <div key={message} className="text-muted-foreground text-sm">
                    {message}
                  </div>
                ))}
              </div>
            )}
            {requiredDocuments.length > 0 && (
              <div className="space-y-1">
                <span className="text-accent-cyan text-sm font-semibold">Required documents</span>
                {requiredDocuments.map((doc) => (
                  <div key={doc.documentType} className="text-muted-foreground text-sm">
                    <span className="font-medium">
                      {CORGI_REQUIRED_DOC_LABELS[doc.documentType] ?? doc.documentType}:
                    </span>{' '}
                    <span
                      className={
                        doc.status === 'required'
                          ? 'text-error'
                          : doc.status === 'waived'
                            ? 'text-accent-green'
                            : undefined
                      }
                    >
                      {CORGI_REQUIRED_DOC_STATUS_LABELS[doc.status] ?? doc.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {renderPortalLink()}
              {renderScreenshotsDialog()}
              {renderSendCorgiDocumentsButton()}
              {renderResubmitActions()}
            </div>
            {children && <div className="mt-4 w-full">{children}</div>}
          </div>
        )
      }

      if (isCorgiManualReview) {
        return (
          <div className="flex flex-col gap-4">
            <div className="text-general-foreground space-y-1">
              <div className="text-md font-semibold">
                {indication.vendorName}
                {renderBrokerBadge()}
                {renderOutOfDateBadge()}
                {renderCorgiDateBadges()}
              </div>
              <div className="text-warning-d1 text-md font-semibold">Under Review</div>
              <div className="text-muted-foreground text-sm">
                A Corgi underwriter is reviewing this submission. You'll be notified when it's resolved.
              </div>
            </div>
            {renderEstimate()}
            {renderStaffTermsNote()}
            {Object.keys(manualReviewReasons).length > 0 && (
              <div className="space-y-1">
                <span className="text-accent-cyan text-sm font-semibold">Review reasons</span>
                {Object.entries(manualReviewReasons).map(([coverage, reasons]) => (
                  <div key={coverage} className="text-muted-foreground text-sm">
                    <span className="font-medium">{CORGI_COVERAGE_LABELS[coverage] ?? coverage}:</span>{' '}
                    {(reasons as string[]).map((r) => CORGI_MANUAL_REVIEW_LABELS[r] ?? r).join(', ')}
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {renderPortalLink()}
              {renderScreenshotsDialog()}
              {renderResubmitActions()}
            </div>
            {children && <div className="mt-4 w-full">{children}</div>}
          </div>
        )
      }

      if (isRocklakeSuspended) {
        return (
          <div className="flex flex-col gap-4">
            <div className="text-general-foreground space-y-1">
              <div className="text-md font-semibold">
                {indication.vendorName}
                {renderBrokerBadge()}
                {renderOutOfDateBadge()}
                {renderCorgiDateBadges()}
              </div>
              <div className="text-warning-d1 text-md font-semibold">Info required</div>
              {indication.additionalInfoRequested && (
                <div className="text-muted-foreground text-sm">{indication.additionalInfoRequested}</div>
              )}
            </div>
            {renderEstimate()}
            {renderStaffTermsNote()}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {renderPortalLink()}
              {renderPortalNoteLink()}
              {renderScreenshotsDialog()}
              {renderQuoteDocumentsDialog()}
              {renderResubmitActions()}
            </div>
            {children && <div className="mt-4 w-full">{children}</div>}
          </div>
        )
      }

      return (
        <div className="flex flex-col gap-4">
          {/* Same place every other state carries it. An indication can sit here
              indefinitely — a carrier that never answers, or a manual one nothing
              will ever fill in — and staff need a way into it to delete it. */}
          <IndicationHeader
            actions={
              isCaiAdmin &&
              !hideViewDetails && (
                <Button size="sm" variant="link" onClick={() => onViewDetails(indication, indication)}>
                  View details
                </Button>
              )
            }
          >
            <div className="text-general-foreground space-y-1">
              <div className="text-md font-semibold">
                {indication.vendorName}
                {renderBrokerBadge()}
                {renderOutOfDateBadge()}
                {renderCorgiDateBadges()}
              </div>
              <div className="text-muted-foreground text-md">
                {isRocklakeReleased
                  ? 'Quote received, pricing pending'
                  : estimate
                    ? 'Awaiting the carrier price'
                    : 'Awaiting estimate'}
              </div>
            </div>
          </IndicationHeader>
          {renderEstimate()}
          {renderStaffTermsNote()}
          <div className="flex items-center gap-2">
            <DotSpinner />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {renderPortalLink()}
            {renderScreenshotsDialog()}
            {renderQuoteDocumentsDialog()}
            {renderResubmitActions()}
          </div>
          {children && <div className="mt-4 w-full">{children}</div>}
        </div>
      )
    }

    return (
      <>
        <div className="min-w-0">
          <IndicationHeader
            actions={
              <>
                {showQuotes && activeQuote?.status === InsuranceQuoteStatus.QUOTED && (
                  <span className="text-accent-green">Bindable quote</span>
                )}
                {showQuotes && activeQuote?.status === InsuranceQuoteStatus.REQUEST_TO_BIND && (
                  <span className="text-accent-green">Requested to bind</span>
                )}
                {showQuotes && activeQuote?.status === InsuranceQuoteStatus.BOUND && (
                  <span className="text-accent-green">Bound</span>
                )}
                {showQuotes && (activeQuote?.status === InsuranceQuoteStatus.PENDING || !activeQuote) && (
                  <span className="text-warning-d1">
                    {isCorgiNeedsInfo
                      ? 'Needs Information'
                      : isCorgiManualReview
                        ? 'Under Review'
                        : 'Awaiting quote'}
                  </span>
                )}
                {showQuotes && activeQuote?.status === InsuranceQuoteStatus.DECLINED && (
                  <span className="text-error">Declined quote</span>
                )}
                {/* The document link, the carrier deep link, the screenshots
                    and every indication CTA now live in the details sidebar —
                    on a narrow column they collided with the carrier name and
                    the quote status right beside them. */}
                <span className="flex items-center gap-0">
                  {!hideViewDetails && (
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => onViewDetails(indicationOrQuote, indication)}
                    >
                      View details
                    </Button>
                  )}
                  {checkboxVisible && (
                    <Checkbox
                      checked={isSelected}
                      className="size-5"
                      onClick={() => onToggleSelect(indication)}
                    />
                  )}
                </span>
              </>
            }
          >
            <span className="text-md text-general-foreground font-semibold">
              {indication.vendorName}
              {indication.dateApplied && (
                <Badge variant="default" className="ml-2 text-xs">
                  Applied
                </Badge>
              )}
              {renderBrokerBadge()}
              {renderOutOfDateBadge()}
              {renderCorgiDateBadges()}
            </span>
            <div className="text-md text-muted-foreground mt-1 font-semibold">
              Estimation for {indication.metadata.truckCount}{' '}
              {pluralize('truck', indication.metadata.truckCount)}
            </div>
          </IndicationHeader>
          <div className="mt-2 flex flex-col gap-2">
            <div className={cn('mt-3 grid gap-2', gridCols)}>
              <span className="text-accent-cyan col-span-2 text-sm font-semibold">Premium breakdown</span>
              {showPerTruck && <span className="text-muted-foreground pl-[50px] text-sm">Per truck</span>}
            </div>
            {indicationOrQuote.coverages.map((coverage) => (
              <div key={coverage.label} className={cn('mt-1 grid gap-2', gridCols)}>
                <span>{coverage.label}</span>
                <span className="text-black">
                  {coverage.premium === null ? 'Included' : formatCurrency(coverage.premium)}
                </span>
                {showPerTruck && (
                  <span className="text-muted-foreground pl-[50px]">
                    {coverage.isPricePerTruck && coverage.premium !== null
                      ? formatCurrency(coverage.premiumPerUnit || coverage.premium / truckCount)
                      : '—'}
                  </span>
                )}
              </div>
            ))}

            {isStaff &&
              Array.isArray(indication.metadata.excludedCoverages) &&
              indication.metadata.excludedCoverages.length > 0 && (
                <div className="mt-3 space-y-1">
                  <span className="text-accent-cyan text-sm font-semibold">Excluded coverages</span>
                  {indication.metadata.excludedCoverages.map((exc: { coverage: string; reason: string }) => (
                    <div key={exc.coverage} className="text-muted-foreground text-sm">
                      <span className="font-medium">
                        {CW_COVERAGE_LABELS[exc.coverage] ?? exc.coverage.toUpperCase()}:
                      </span>{' '}
                      {exc.reason}
                    </div>
                  ))}
                </div>
              )}

            {showQuotes && isCorgiManualReview && Object.keys(manualReviewReasons).length > 0 && (
              <div className="mt-3 space-y-1">
                <span className="text-accent-cyan text-sm font-semibold">Under review</span>
                {Object.entries(manualReviewReasons).map(([coverage, reasons]) => (
                  <div key={coverage} className="text-muted-foreground text-sm">
                    <span className="font-medium">{CORGI_COVERAGE_LABELS[coverage] ?? coverage}:</span>{' '}
                    {reasons.map((r) => CORGI_MANUAL_REVIEW_LABELS[r] ?? r).join(', ')}
                  </div>
                ))}
              </div>
            )}

            {showQuotes &&
              activeQuote?.status === InsuranceQuoteStatus.DECLINED &&
              Object.keys(declinedCoverages).length > 0 && (
                <div className="mt-3 space-y-1">
                  <span className="text-accent-cyan text-sm font-semibold">Declined coverages</span>
                  {Object.entries(declinedCoverages).map(([coverage, reason]) => (
                    <div key={coverage} className="text-muted-foreground text-sm">
                      <span className="font-medium">{CORGI_COVERAGE_LABELS[coverage] ?? coverage}:</span>{' '}
                      {CORGI_DECLINED_LABELS[reason] ?? reason}
                    </div>
                  ))}
                </div>
              )}

            {indicationOrQuote.oneTimeFees.length > 0 && (
              <>
                <Separator className="mt-2 mb-2" />
                <span className="text-accent-cyan text-sm font-semibold">One-time fees</span>
                {indicationOrQuote.oneTimeFees.map((fee) => (
                  <div key={fee.id} className={cn('mt-1 grid gap-2', gridCols)}>
                    <span>{fee.label}</span>
                    <span className="text-black">{formatCurrency(fee.amount)}</span>
                  </div>
                ))}
              </>
            )}

            <Separator className="mt-2 mb-2" />

            <div className={cn('grid gap-2', gridCols)}>
              <span className="text-muted-foreground">Estimated total</span>
              <span className="text-general-foreground">{formatCurrency(indicationOrQuote.premium)}</span>
            </div>
            {(canResubmit || canSyncCorgi) && <div className="mt-3">{renderResubmitActions()}</div>}
          </div>
        </div>
        {!needsAttestation && customerOwesTerms && (
          <div className="mt-4 w-full">{renderStaffTermsNote()}</div>
        )}
        {needsAttestation && (
          <div className="mt-4 w-full">
            <Banner
              variant="warning"
              small
              align="center"
              action={
                <Button size="sm" onClick={() => setAttestationOpen(true)}>
                  Review
                </Button>
              }
            >
              This carrier requires acceptance of terms.
            </Banner>
            <CarrierAttestationDialog
              orgId={orgId!}
              carrierName={indication.vendorName}
              open={attestationOpen}
              onOpenChange={setAttestationOpen}
              onAccepted={() => setAttestationOpen(false)}
            />
          </div>
        )}
        {children && <div className="mt-4 w-full">{children}</div>}
      </>
    )
  }

  return (
    <Card key={indication.id} className={isSelected ? 'border-ink-900' : ''}>
      <CardContent className="text-muted-foreground font-semibold">
        <div className={cn(hideLogo ? '' : 'grid grid-cols-[48px_1fr] gap-6')}>
          {!hideLogo && (
            <img src={indication.iconUrl} alt={indication.iconAlt} className="h-12 w-12 object-contain" />
          )}
          {/* One grid item: the fragment would otherwise spill into the 48px logo column. */}
          <div className="min-w-0">
            {renderContent()}
            {/* The indication's own clock, not the active quote's — each quote row carries that. */}
            {isStaff && (
              <div className="mt-4">
                <IndicationTimestamps
                  dateSubmitted={indication.dateSubmitted}
                  dateCreated={indication.dateCreated}
                  dateUpdated={indication.dateUpdated}
                />
                {/* The date belongs to whichever offer's price the card shows. */}
                {indication.status === InsuranceIndicationStatus.GENERATED &&
                  indicationOrQuote.pricedStartDate && (
                    <div className="text-muted-foreground text-xs font-normal">
                      Starts {formatDate(indicationOrQuote.pricedStartDate)}
                    </div>
                  )}
                {corgiLastSyncedAt && (
                  <div className="text-muted-foreground text-xs font-normal">
                    {`Last checked with Corgi ${formatDistanceToNow(new Date(corgiLastSyncedAt), { addSuffix: true })}`}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
